import moment from 'moment'
import {Spinner} from 'react-bootstrap'
import {useIntl} from 'react-intl'
import {NumericFormat} from 'react-number-format'
import {useNavigate, useParams, useSearchParams} from 'react-router-dom'
import {Table} from 'src/feature/bite/component'
import {currency} from 'src/feature/bite/helpers/currency'
import Swal from 'sweetalert2'
import {useDecreaseCredit, useIncreaseCredit} from '../../context/useMutation'
import {useGetPackageDetail, useGetPackagePaymentList} from '../../context/useQuery'
import {usePackageDetail} from '../../hook/package'

export default function MemberPackageDetail() {
  const intl = useIntl()
  const [queryParameters] = useSearchParams()
  const {packageId, memberId} = useParams() as any
  const navigate = useNavigate()

  const {isLoading} = useGetPackageDetail(packageId)
  const {isLoading: isLoadingPaymentList} = useGetPackagePaymentList(packageId)
  const {detail, paymentList, paymentListPaging, paymentListPagingSet} = usePackageDetail()
  const {mutateAsync: decreaseCredit, isLoading: isDecreasing} = useDecreaseCredit({
    packageId: packageId,
  })
  const {mutateAsync: increaseCredit, isLoading: isIncreasing} = useIncreaseCredit({
    packageId: packageId,
  })

  const handleDecreaseCredit = async (id: string) => {
    Swal.fire({
      title: `Do you want to proceed with deducting this credit from the member's subscription?`,
      text: 'This action cannot be undone.',
      iconHtml: '<i class="bi bi-x-circle-fill text-danger fs-4x"></i>',
      customClass: {
        icon: 'text-primary border-none border-0',
        title: 'fs-4',
        container: 'py-0 my-0',
        htmlContainer: 'pt-3 mt-3',
        confirmButton: 'me-10',
        actions: 'd-flex justify-content-between',
      },
      showCancelButton: true,
      confirmButtonText: intl.formatMessage({id: 'YES'}),
      cancelButtonText: intl.formatMessage({id: 'NO'}),
      confirmButtonColor: '#5cb85c',
      cancelButtonColor: '#F1416C',
    }).then(async (result) => {
      if (result.isConfirmed) {
        await decreaseCredit({id})
      }
    })
  }

  const handleIncreaseCredit = async (id: string) => {
    Swal.fire({
      title: `Do you want to proceed with this credit update for the member?`,
      text: 'This action cannot be undone.',
      iconHtml: '<i class="bi bi-x-circle-fill text-danger fs-4x"></i>',
      customClass: {
        icon: 'text-primary border-none border-0',
        title: 'fs-4',
        container: 'py-0 my-0',
        htmlContainer: 'pt-3 mt-3',
        confirmButton: 'me-10',
        actions: 'd-flex justify-content-between',
      },
      showCancelButton: true,
      confirmButtonText: intl.formatMessage({id: 'YES'}),
      cancelButtonText: intl.formatMessage({id: 'NO'}),
      confirmButtonColor: '#5cb85c',
      cancelButtonColor: '#F1416C',
    }).then(async (result) => {
      if (result.isConfirmed) {
        await increaseCredit({id})
      }
    })
  }

  const columns = [
    {
      key: 'status',
      dataIndex: 'status',
      title: 'Status',
      render: (text: number) => {
        switch (text) {
          case 1:
            return <span className='badge-primary'>Paid</span>
          case 2:
            return <span className='badge-warning'>Inactive</span>
          case 3:
            return <span className='badge-danger'>Failed</span>
          case 4:
            return <span className='badge-success'>Paid</span>
          case 5:
            return <span className='badge-danger'>Unpaid</span>
          case 6:
            return <span className='badge-warning'>Waiting</span>
          default:
            return <span className='badge-light'>-</span>
        }
      },
    },
    {
      key: 'paymentDate',
      dataIndex: 'paymentDate',
      title: intl.formatMessage({id: 'LABEL.PAYMENT.DATE'}),
      render: (text: string) => {
        return <div className=''>{text ? moment.utc(text).format('DD/MM/YYYY HH:mm') : '-'}</div>
      },
    },
    {
      key: 'amount',
      dataIndex: 'amount',
      title: intl.formatMessage({id: 'CHECKOUT.PURCHASE.TABLE.AMOUNT'}),
      customMonetize: true,
    },
    {
      key: 'channel',
      dataIndex: 'channel',
      title: intl.formatMessage({id: 'CHECKOUT.PURCHASE.TABLE.CHANNEL'}),
      render: (text: number) => {
        switch (text) {
          case 1:
            return <span className='text-primary'>Online</span>
          case 2:
            return <span className='text-primary'>In-Store</span>
          case 3:
            return <span className='text-primary'>Both</span>
          default:
            return <span className='text-primary'>Online</span>
        }
      },
    },
  ]

  return (
    <>
      <div className='px-5 pb-10 d-flex align-items-center gap-3'>
        <i
          className='bi bi-arrow-left text-black fs-2x cursor-pointer'
          onClick={() => {
            navigate(-1)
          }}
        />
        <h2 className='fw-bold text-black m-0 p-0'>Member List</h2>
      </div>
      <div className='card'>
        <div className='card-body'>
          <div className='d-flex flex-column gap-5'>
            <div className='d-flex flex-column gap-5'>
              <h2 className='fw-bold text-black m-0 p-0'>{detail?.packageDetail?.name}</h2>
              <h2 className='fw-bold text-black m-0 p-0'>
                {currency(detail?.packageDetail?.price || 0)}
              </h2>
              <div className='d-flex align-items-center gap-10'>
                <span className='text-black'>
                  Start Date: {moment.utc(detail?.packageDetail?.startDate).format('DD/MM/YYYY')}
                </span>
                <span className='text-black'>
                  End Date: {moment.utc(detail?.packageDetail?.endDate).format('DD/MM/YYYY')}
                </span>
              </div>
              <div className='d-flex gap-5'>
                {detail?.packageItemList?.map((item: any) => (
                  <div className='d-flex flex-column bg-light px-5 py-3 rounded-2 min-w-300px border gap-10'>
                    <div className='d-flex flex-column'>
                      <span className='mb-2 mt-5 text-black fw-bold fs-3'>{item.name}</span>
                      <span className='text-primary fs-5'>
                        Total Credit: <span className='fw-semibold'>{item.count}</span>
                      </span>
                    </div>

                    <div className='d-flex align-items-center gap-5'>
                      {isIncreasing ? (
                        <Spinner animation='border' role='status'>
                          <span className='visually-hidden'>Loading...</span>
                        </Spinner>
                      ) : (
                        <button
                          className='btn btn-success btn-lg btn-icon'
                          disabled={
                            isDecreasing ||
                            isLoading ||
                            isIncreasing ||
                            detail?.packageDetail?.status === 2 ||
                            detail?.packageDetail?.status === 3 ||
                            detail?.packageDetail?.status === 4 ||
                            detail?.packageDetail?.status === 5
                          }
                          onClick={async () => {
                            if (
                              detail?.packageDetail?.status === 2 ||
                              detail?.packageDetail?.status === 3 ||
                              detail?.packageDetail?.status === 4 ||
                              detail?.packageDetail?.status === 5
                            )
                              return
                            await handleIncreaseCredit(item.id)
                          }}
                        >
                          <i className='bi bi-plus fw-bold fs-2x' />
                        </button>
                      )}
                      <div className='d-flex flex-column align-items-center bg-white px-5 py-3 rounded-2 border'>
                        <span className='fs-5 fw-semibold'>Remaining</span>
                        <p className='fs-3 fw-bold p-0 m-0'>{item.remainingCount}</p>
                      </div>

                      {isDecreasing ? (
                        <Spinner animation='border' role='status'>
                          <span className='visually-hidden'>Loading...</span>
                        </Spinner>
                      ) : (
                        <button
                          className='btn btn-danger btn-lg btn-icon'
                          disabled={
                            isDecreasing ||
                            isLoading ||
                            isIncreasing ||
                            item?.remainingCount === 0 ||
                            detail?.packageDetail?.status === 2 ||
                            detail?.packageDetail?.status === 3 ||
                            detail?.packageDetail?.status === 4 ||
                            detail?.packageDetail?.status === 5
                          }
                          onClick={async () => {
                            if (item?.remainingCount === 0) return
                            if (
                              detail?.packageDetail?.status === 2 ||
                              detail?.packageDetail?.status === 3 ||
                              detail?.packageDetail?.status === 4 ||
                              detail?.packageDetail?.status === 5
                            )
                              return
                            await handleDecreaseCredit(item.id)
                          }}
                        >
                          <i className='bi bi-dash fw-bold fs-2x' />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className='card mt-10'>
        <div className='card-body'>
          <h2 className='fw-bold text-black m-0 p-0 fs-3 pb-5'>
            {intl.formatMessage({id: 'LABEL.PRICING.DETAILS'})}
          </h2>
          <div className='row'>
            <div className='col-md-3 d-flex flex-column'>
              <label className='mb-2'>{intl.formatMessage({id: 'LABEL.TOTAL.PRICE'})}</label>
              <NumericFormat
                className='fs-5 fw-semibold'
                displayType='text'
                prefix='AED '
                decimalScale={2}
                thousandSeparator={true}
                placeholder='AED 1.00'
                fixedDecimalScale={true}
                value={detail?.pricingDetail?.price}
                style={{width: '25%'}}
              />
            </div>
            <div className='col-md-3 d-flex flex-column'>
              <label className='mb-2'>
                {intl.formatMessage({id: 'LABEL.SUBSCRIPTION.OPTIONS'})}
              </label>
              <span className='fs-5 fw-semibold'>
                {detail?.pricingDetail?.subscriptionOptions === 1 ? 'Monthly' : 'Annual'}
              </span>
            </div>
            <div className='col-md-3 d-flex flex-column'>
              <label className='mb-2'>{intl.formatMessage({id: 'LABEL.COUNT'})}</label>
              <span className='fs-5 fw-semibold'>{detail?.pricingDetail?.count}</span>
            </div>
            <div className='col-md-3 d-flex flex-column'>
              <label className='mb-2'>
                {intl.formatMessage({id: 'LABEL.SUBSCRIPTION.RECURRING'})}
              </label>
              <NumericFormat
                className='fs-5 fw-semibold'
                displayType='text'
                allowNegative={false}
                suffix=' AED'
                decimalScale={2}
                thousandSeparator={true}
                placeholder='AED 1.00'
                fixedDecimalScale={true}
                aria-describedby='basic-addon2'
                value={(
                  (detail?.pricingDetail?.price || 0) / (detail?.pricingDetail?.count || 1) || 0
                ).toFixed(2)}
                style={{width: '25%'}}
              />
            </div>
          </div>
          <div className='row mt-5'>
            <div className='col-md-3 d-flex flex-column'>
              <label className='mb-2'>
                {intl.formatMessage({id: 'LABEL.SUBSCRIPTION.TAX.RATE'})}
              </label>
              <NumericFormat
                className='fs-5 fw-semibold'
                displayType='text'
                allowNegative={false}
                suffix=' %'
                decimalScale={2}
                thousandSeparator={true}
                placeholder='AED 1.00'
                fixedDecimalScale={true}
                aria-describedby='basic-addon2'
                value={detail?.pricingDetail?.taxRate}
                style={{width: '25%'}}
              />
            </div>
            <div className='col-md-3 d-flex flex-column'>
              <label className='mb-2'>
                {intl.formatMessage({id: 'LABEL.SUBSCRIPTION.PURCHASE.TYPE'})}
              </label>
              <span className='fs-5 fw-semibold'>
                {detail?.pricingDetail?.purchaseType === 1 ? 'Vasco App' : '-'}
              </span>
            </div>
            <div className='col-md-3 d-flex flex-column'>
              <label className='mb-2'>
                {intl.formatMessage({id: 'LABEL.SUBSCRIPTION.PURCHASE.CANCELLATION.FEE'})}
              </label>
              <NumericFormat
                className='fs-5 fw-semibold'
                displayType='text'
                allowNegative={false}
                suffix=' AED'
                decimalScale={2}
                thousandSeparator={true}
                placeholder='AED 1.00'
                fixedDecimalScale={true}
                aria-describedby='basic-addon2'
                value={detail?.pricingDetail?.cancellationFee || 0}
                style={{width: '25%'}}
              />
            </div>
          </div>
        </div>
      </div>

      <div className='card mt-10'>
        <div className='card-body'>
          <h2 className='fw-bold text-black m-0 p-0 fs-3 pb-5'>
            {intl.formatMessage({id: 'LABEL.PAYMENT.DETAILS'})}
          </h2>
          <div className='table-responsive'>
            <Table
              columns={columns}
              loading={isLoadingPaymentList}
              dataSource={paymentList?.response?.dataList}
              pagination={{
                current: paymentList?.response?.paging?.currentPageNumber || 1,
                pageSize: paymentList?.response?.paging?.pageSize || 10,
                total: paymentList?.response?.paging?.totalCount || 0,
                onChange: (page: any) => {
                  const {pageSize, current} = page
                  const currentPage = pageSize !== paymentListPaging.pageSize ? 1 : current

                  paymentListPagingSet({
                    ...paymentListPaging,
                    currentPageNumber: currentPage,
                    pageSize: pageSize,
                  })
                },
              }}
            />
          </div>
        </div>
      </div>
    </>
  )
}
